import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Box,
  ListItem,
  ListItemButtonProps as ListItemButtonPropsRoot,
  ListItemButton as ListItemButtonRoot,
  ListItemIcon,
  ListItemProps,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReactNode, useContext, useEffect } from 'react'
import { Link, Location, useLocation } from 'react-router-dom'

import { SidebarContext } from 'features/layout/components/Sidebar/SidebarContext'
import { SidebarItem } from 'features/layout/components/Sidebar/types'
import { isSidebarItemEmpty } from 'features/layout/components/Sidebar/utils'
import { ListItemButtonProps } from './types'
import { isSelected } from './utils'

export const ListItemButton = ({
  collapsedX,
  collapsedY,
  creamBackground,
  disabled,
  endAdornment,
  group,
  icon,
  isSelectable,
  newTab,
  onClick,
  onCollapseY,
  selectablePaths,
  text,
  to,
  withTooltip,
  ...props
}: ListItemButtonProps): JSX.Element => {
  const location: Location = useLocation()
  const { selectedItemRoutePath, setSelectedItemRoutePath } = useContext(SidebarContext)

  const isSingleItemSelected: boolean = isSelected({ isSelectable, location, selectablePaths, to })

  const anySubItemMatchesSelectedItem = (subItems: SidebarItem[]) =>
    subItems.some(
      (subItem: SidebarItem): boolean =>
        !isSidebarItemEmpty(subItem) && subItem.to === selectedItemRoutePath,
    )

  const hasGroupItemSelectedAnyChildren: boolean =
    group !== undefined &&
    group.items.some((item: SidebarItem): boolean => {
      if (isSidebarItemEmpty(item)) {
        return false
      }

      if (item.to === selectedItemRoutePath) {
        return true
      } else if (item.subItems !== undefined && !isSidebarItemEmpty(item)) {
        return anySubItemMatchesSelectedItem(item.subItems)
      } else return false
    })

  useEffect(() => {
    if (isSingleItemSelected && selectedItemRoutePath === undefined) {
      if (setSelectedItemRoutePath !== undefined) {
        setSelectedItemRoutePath(to)
      }
    }
  }, [])

  const ListItemStyled = ({ ...localProps }: ListItemProps) => (
    <ListItem
      {...props}
      {...localProps}
      onClick={(event: React.MouseEvent<HTMLLIElement>): void => {
        if (disabled) return

        if (onCollapseY) {
          onCollapseY(event)
        } else {
          if (setSelectedItemRoutePath !== undefined) {
            setSelectedItemRoutePath(to)
          }
        }

        if (onClick) {
          onClick(event)
        }
      }}
      sx={{
        ...props.sx,
        '.Mui-selected': {
          backgroundColor: '#F5F5F5',
        },
        backgroundColor: theme =>
          creamBackground ? theme.palette.cream.main : theme.palette.white.main,
        color: '#404040',
        p: 0,
      }}
    />
  )

  const ListItemButtonStyled = ({ ...props }: ListItemButtonPropsRoot) => (
    <ListItemButtonRoot
      {...props}
      {...(to && {
        component: Link,
        rel: newTab ? 'noopener noreferrer' : undefined,
        selected: isSingleItemSelected,
        target: newTab ? '_blank' : undefined,
        to: to,
      })}
      disabled={disabled}
    />
  )

  const ListItemIconStyled = (): JSX.Element => (
    <ListItemIcon
      sx={{
        minWidth: 'auto',
        mr: 2,
      }}
    >
      {icon}
    </ListItemIcon>
  )

  const TypographyStyled = (): JSX.Element => {
    const isUsedInSidebar: boolean = collapsedX !== undefined

    const Content: JSX.Element = (
      <Typography
        sx={theme => ({
          fontWeight:
            isUsedInSidebar && (selectedItemRoutePath === to || hasGroupItemSelectedAnyChildren)
              ? theme.typography.fontWeightMedium
              : theme.typography.fontWeightRegular,
          pl: icon ? 0 : 5,
        })}
      >
        {text}
      </Typography>
    )

    return withTooltip ? <Tooltip title={text}>{Content}</Tooltip> : Content
  }

  const EndAdornment = (): JSX.Element => {
    const render = (): ReactNode => {
      if (endAdornment) {
        return endAdornment
      } else if (!collapsedX && onCollapseY) {
        return collapsedY ? <ExpandLess /> : <ExpandMore />
      }
    }

    return (
      <Box
        sx={{
          display: 'flex',
          ml: 'auto',
        }}
      >
        {render()}
      </Box>
    )
  }

  return (
    <ListItemStyled>
      <ListItemButtonStyled>
        {icon && <ListItemIconStyled />}
        {!collapsedX && <TypographyStyled />}
        <EndAdornment />
      </ListItemButtonStyled>
    </ListItemStyled>
  )
}
